import {useCallback} from 'react';

import {
    SearchHolidays$Request,
    searchHolidaysFailure,
    searchHolidaysRequest,
    searchHolidaysSuccess,
    AddHoliday$Request,
    addHolidayFailure,
    addHolidayRequest,
    addHolidaySuccess,
    UpdateHoliday$Request,
    updateHolidayFailure,
    updateHolidayRequest,
    updateHolidaySuccess,
    DeleteHoliday$Request,
    deleteHolidayFailure,
    deleteHolidayRequest,
    deleteHolidaySuccess,
} from '/src/openapi/ccdb-bff/apis/HolidaysApi';
import {Holiday} from '/src/openapi/ccdb-bff/models/Holiday';
import {useAppDispatch, useAppSelector} from '/src/store/hooks';
import {ROOT} from '/src/middleware/api';

export const REGION_OPTIONS = [
    {value: 'UK', label: 'UK'},
    {value: 'US', label: 'US'},
    {value: 'EU', label: 'EU'},
    {value: 'JP', label: 'JP'},
    {value: 'AU', label: 'AU'},
    {value: 'CA', label: 'CA'},
    {value: 'HK', label: 'HK'},
    {value: 'SG', label: 'SG'},
];

export const DEFAULT_PAGE_SIZE = 20;

export const useHolidaySearch = () => {
    const dispatch = useAppDispatch();
    const {searchResults, loading, saving, deleting, error, successMessage} = useAppSelector(state => state.ccdbHoliday);

    const doSearch = useCallback(async (params: SearchHolidays$Request) => {
        const meta = {
            region: params.region,
            year: params.year,
            page: params.page ?? 0,
            size: params.size ?? DEFAULT_PAGE_SIZE,
        };

        dispatch(searchHolidaysRequest({data: undefined, meta, error: false}));

        try {
            const queryParams = new URLSearchParams();
            queryParams.set('region', meta.region);
            if (meta.year != null && meta.year > 0) {
                queryParams.set('year', String(meta.year));
            }
            queryParams.set('page', String(meta.page));
            queryParams.set('size', String(meta.size));

            const response = await fetch(`${ROOT}/api/holidays/search?${queryParams.toString()}`);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({code: response.status, message: `Holiday search failed (HTTP ${response.status})`}));
                dispatch(searchHolidaysFailure({data: errorData, meta, error: true}));
                return;
            }

            const data = await response.json();

            // Normalize: if response is an array, wrap in expected format
            const holidays: Holiday[] = Array.isArray(data) ? data : (data.holidays || []);
            dispatch(searchHolidaysSuccess({
                data: {
                    holidays,
                    totalElements: Array.isArray(data) ? data.length : (data.totalElements ?? holidays.length),
                    totalPages: data.totalPages ?? 1,
                    page: data.page ?? 0,
                    size: data.size ?? DEFAULT_PAGE_SIZE,
                },
                meta,
                error: false,
            }));
        } catch (err) {
            dispatch(searchHolidaysFailure({
                data: {code: 0, message: err instanceof Error ? err.message : 'Network error occurred'},
                meta,
                error: true,
            }));
        }
    }, [dispatch]);

    const doAdd = useCallback(async (params: AddHoliday$Request) => {
        dispatch(addHolidayRequest({data: undefined, meta: params, error: false}));

        try {
            const response = await fetch(`${ROOT}/api/holidays`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
                body: JSON.stringify({
                    holidayName: params.holidayName,
                    holidayDate: params.holidayDate,
                    region: params.region,
                    isActive: params.isActive ?? true,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({code: response.status, message: `Add holiday failed (HTTP ${response.status})`}));
                dispatch(addHolidayFailure({data: errorData, meta: params, error: true}));
                return false;
            }

            const holiday: Holiday = await response.json();
            dispatch(addHolidaySuccess({data: holiday, meta: params, error: false}));
            return true;
        } catch (err) {
            dispatch(addHolidayFailure({
                data: {code: 0, message: err instanceof Error ? err.message : 'Network error occurred'},
                meta: params,
                error: true,
            }));
            return false;
        }
    }, [dispatch]);

    const doUpdate = useCallback(async (params: UpdateHoliday$Request) => {
        dispatch(updateHolidayRequest({data: undefined, meta: params, error: false}));

        try {
            const response = await fetch(`${ROOT}/api/holidays/${encodeURIComponent(String(params.id))}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
                body: JSON.stringify({
                    holidayName: params.holidayName,
                    holidayDate: params.holidayDate,
                    region: params.region,
                    isActive: params.isActive ?? true,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({code: response.status, message: `Update failed (HTTP ${response.status})`}));
                dispatch(updateHolidayFailure({data: errorData, meta: params, error: true}));
                return false;
            }

            const holiday: Holiday = await response.json();
            dispatch(updateHolidaySuccess({data: holiday, meta: params, error: false}));
            return true;
        } catch (err) {
            dispatch(updateHolidayFailure({
                data: {code: 0, message: err instanceof Error ? err.message : 'Network error occurred'},
                meta: params,
                error: true,
            }));
            return false;
        }
    }, [dispatch]);

    const doDelete = useCallback(async (params: DeleteHoliday$Request) => {
        dispatch(deleteHolidayRequest({data: undefined, meta: params, error: false}));

        try {
            const response = await fetch(`${ROOT}/api/holidays/${encodeURIComponent(String(params.id))}`, {
                method: 'DELETE',
                headers: {'Accept': 'application/json'},
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({code: response.status, message: `Delete failed (HTTP ${response.status})`}));
                dispatch(deleteHolidayFailure({data: errorData, meta: params, error: true}));
                return false;
            }

            dispatch(deleteHolidaySuccess({data: undefined, meta: params, error: false}));
            return true;
        } catch (err) {
            dispatch(deleteHolidayFailure({
                data: {code: 0, message: err instanceof Error ? err.message : 'Network error occurred'},
                meta: params,
                error: true,
            }));
            return false;
        }
    }, [dispatch]);

    return {
        searchResults,
        loading,
        saving,
        deleting,
        error,
        successMessage,
        doSearch,
        doAdd,
        doUpdate,
        doDelete,
    };
};
